import { catchError, map, switchMap, withLatestFrom, debounceTime, take, mapTo } from 'rxjs/operators';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { IList, IListState } from '@aston/foundation/models';
import { TranslateService } from '@ngx-translate/core';
import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { saveAs } from 'file-saver';
import { Observable, of } from 'rxjs';

import { SuperCustomersListService } from '../../customers-module/services/super-customers-list.service';
import { CustomersListService } from '../../customers-module/services/customers-list.service';
import * as SuperCustomerPageStoreSelectors from '../super-customer-page-store/selectors';
import { CorrelationParams, aggregate } from '../models/correlated-actions.model';
import { ISuperCustomerItem } from '../../customers-module/models';
import * as appActions from '../app-store/actions';
import { AppStoreActions } from '../app-store';

import { getSuperCustomerById } from './helpers';
import * as featureSelectors from './selectors';
import * as featureActions from './actions';



@Injectable({providedIn: 'root'})
export class SuperCustomersStoreEffects {
	private actions$ = inject(Actions);
	private store = inject(Store);
	private customersService = inject(CustomersListService);
	private superCustomersService = inject(SuperCustomersListService);
	protected translateService = inject(TranslateService);


	switchMapTo<T>(
		groupedSwitch: (p: IListState<any>) => Observable<T>,
		singleSwitch: (p: IListState<any>) => Observable<T>,
	): Observable<T> {
		return of(null).pipe(
			withLatestFrom(
				this.store.select(featureSelectors.selectUseGroupedCustomers),
				this.store.select(featureSelectors.selectListPaging),
				this.store.select(featureSelectors.selectListFilters),
			),
			switchMap(([_, grouped, paging, filters]) => grouped
				? groupedSwitch({ ...paging, filters })
				: singleSwitch({ ...paging, filters })
			)
		)
	}

	initEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.InitRequest),
		withLatestFrom(this.store),
		map(([action]) => {
			const correlationParams: CorrelationParams = action.correlationParams;
			correlationParams.parentActionType = action.type;
			return featureActions.LoadSuperCustomersRequest(correlationParams);
		}),
		catchError(error => of(featureActions.InitFailure({ error })))
	));

	afterInitEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.AfterInitRequest),
		// Wait for the next InitRequest to happen
		switchMap(action => this.actions$.pipe(ofType(featureActions.InitRequest), take(1), mapTo(action))),
		// Then dispatch given actions
		switchMap(action => action.dispatch),
	));

	initSuccessEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.InitRequest),
		aggregate(
			this.actions$.pipe(ofType(featureActions.LoadSuperCustomersSuccess)),
			this.actions$.pipe(ofType(featureActions.InitFailure)),
		),
		map(_ => featureActions.InitSuccess()),
		catchError(error => of(featureActions.InitFailure({ error })))
	));


	loadRequestEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.LoadSuperCustomersRequest),
		debounceTime(500),
		switchMap(action => this.switchMapTo(
			s => this.superCustomersService.getCustomers(s),
			s => this.customersService.getCustomers(s) as unknown as Observable<IList<ISuperCustomerItem>>,
		).pipe(
			map(list => featureActions.LoadSuperCustomersSuccess({ list, correlationParams: action.correlationParams })),
			catchError(error =>
				of(featureActions.LoadSuperCustomersFailure({ error }))
			)
		))
	));

	exportRequestEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.ExportSuperCustomersRequest),
		debounceTime(500),
		switchMap(action => this.switchMapTo(
			s => this.superCustomersService.export(s),
			s => this.customersService.export(s),
		).pipe(
			map(response => {
				saveAs(response.blob, response.fileName);
				return featureActions.ExportSuperCustomersSuccess({ correlationParams: action.correlationParams });
			}),
			catchError(error => of(
				featureActions.ExportSuperCustomersFailure({ error }),
				AppStoreActions.ToastError('Errors.RetryableError')
			))
		))
	));

	updateFilterEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.UpdateSuperCustomersSettings, featureActions.ToggleGroupedCustomers, featureActions.AddSuperCustomersFilters, featureActions.RemoveSuperCustomersFilters),
		map(_ => featureActions.LoadSuperCustomersRequest())
	));

	loadSuccessEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.LoadSuperCustomersSuccess),
		map(_ => appActions.StartScrollToTop())
	));

	updateDebtorsSettings$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.UpdateCustomersSettings),
		map(action => featureActions.LoadCustomersRequest({ id: action.id }))
	));

	loadAttachedDebtorsEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.LoadAttachedCustomersRequest),
		withLatestFrom(
			this.store.select(featureSelectors.selectState),
			this.store.select(SuperCustomerPageStoreSelectors.selectAttachedDebtorsFilters)),
		switchMap(([action, state, filters]) => {
			const superCustomer = getSuperCustomerById(state, action.id);
			const paging = superCustomer.attachedDebtorsListPaging;
			return this.superCustomersService.getAttachedDebtors(superCustomer.id, {...paging, filters }).pipe(
				map(attachedDebtors => featureActions.LoadAttachedCustomersSuccess({ id: action.id, attachedCustomers: attachedDebtors })),
				catchError(error =>
					of(featureActions.LoadAttachedCustomersFailure({ id: action.id, error }))
				)
			);
		}
		)
	));

	updateAttachedDebtorsSettings$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.UpdateAttachedCustomersSettings),
		map(action => featureActions.LoadAttachedCustomersRequest({ id: action.id }))
	));
}
